// Seasonal colours and drifting particles for the Mood Garden
const hash = (s) => { let h = 2166136261; for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619); return (h >>> 0) / 4294967295; };

export const SEASON_LOOK = {
  spring: { back: "#C9E8B8", front: ["#B5DFA0", "#8FCB7E"], grass: "#5FA45A", dots: ["#FFF6D6", "#F7D6E4", "#E4DAF7"], label: "Spring garden", emoji: "🌸" },
  summer: { back: "#D4E6A2", front: ["#C5DC8E", "#A3C66C"], grass: "#7FA84A", dots: ["#FFE89A", "#FFF6D6", "#F9D57E"], label: "Summer garden", emoji: "☀️" },
  autumn: { back: "#C9C489", front: ["#B9B56E", "#9C9553"], grass: "#7E7A3A", dots: ["#F2A65A", "#D9693B", "#F6C343"], label: "Autumn garden", emoji: "🍂" },
  winter: { back: "#E1EAF1", front: ["#F3F7FA", "#DCE6EE"], grass: "#9FB3C2", dots: ["#FFFFFF", "#EAF2F8", "#DDE8F1"], label: "Winter garden", emoji: "❄️" },
};

const Petal = ({ c }) => <ellipse rx="4" ry="2.4" fill={c} opacity="0.9" />;
const LeafBit = ({ c }) => <path d="M0 -8 Q8 -2 0 8 Q-8 -2 0 -8z M0 -8 L0 8" fill={c} stroke="#8A4A22" strokeWidth="0.6" />;
const Flake = () => <circle r="2.8" fill="#fff" stroke="#C3D0DD" strokeWidth="0.6" opacity="0.95" />;

const FALLERS = {
  spring: { n: 14, El: Petal, colors: ["#F7C6D9", "#FBE3EC", "#F2A7C3"], dur: 11 },
  autumn: { n: 22, El: LeafBit, colors: ["#E2793A", "#C9522E", "#E9A23B", "#B5652B"], dur: 9 },
  winter: { n: 34, El: Flake, colors: ["#fff"], dur: 12 },
};

const Fallers = ({ season, w, h }) => {
  const f = FALLERS[season];
  if (!f) return null;
  return Array.from({ length: f.n }, (_, i) => {
    const x = hash(`${season}x${i}`) * w, y = hash(`${season}y${i}`) * (h - 40) + 10;
    const s = 0.7 + hash(`${season}s${i}`) * 0.8;
    return (
      <g key={i} transform={`translate(${x.toFixed(1)},${y.toFixed(1)})`}>
        <g className="mm-fall" style={{ "--fy": `${(-y - 30).toFixed(0)}px`, "--ty": `${(h - y + 30).toFixed(0)}px`, animationDuration: `${(f.dur * (0.7 + hash(`${season}d${i}`) * 0.6)).toFixed(1)}s`, animationDelay: `-${(hash(`${season}a${i}`) * f.dur).toFixed(1)}s` }}>
          <g transform={`scale(${s.toFixed(2)}) rotate(${(hash(`${season}r${i}`) * 360).toFixed(0)})`}><f.El c={f.colors[i % f.colors.length]} /></g>
        </g>
      </g>
    );
  });
};

const Fireflies = ({ w }) => Array.from({ length: 16 }, (_, i) => (
  <circle key={i} className="mm-firefly" cx={hash(`f${i}`) * w} cy={200 + hash(`fy${i}`) * 170} r="3.6" fill="#FFF3A0"
    style={{ animationDelay: `-${(hash(`fd${i}`) * 3).toFixed(1)}s`, filter: "drop-shadow(0 0 4px #FFE45C)" }} />
));

const SnowCap = ({ w }) => (
  <path d={`M0 292 Q260 252 520 287 T${w} 282 L${w} 292 Q520 300 260 266 T0 300z`} fill="#FFFFFF" opacity="0.9" />
);

const FOLIAGE = { spring: ["#F7C6D9", "#F2A7C3", "#FBE3EC"], summer: ["#4E9A4A", "#5FAE58", "#3F8A40"], autumn: ["#E2793A", "#C9522E", "#E9A23B"] };

const Tree = ({ season, x, y, s }) => (
  <g transform={`translate(${x},${y}) scale(${s})`}>
    <path d="M-4 0 L-3 -46 Q0 -50 3 -46 L4 0z" fill="#8B6A4E" />
    {season === "winter" ? (
      <g stroke="#8B6A4E" strokeWidth="2.4" strokeLinecap="round" fill="none">
        <path d="M0 -30 L-18 -50 M0 -38 L16 -58 M-10 -41 L-14 -62 M8 -48 L4 -68 M0 -46 L0 -72" />
        <g stroke="#FFFFFF" strokeWidth="2"><path d="M-18 -51 L-12 -45 M16 -59 L10 -53 M-14 -63 L-12 -57 M4 -69 L5 -63 M0 -73 L0 -67" /></g>
      </g>
    ) : (
      FOLIAGE[season].map((c, i) => <circle key={c} cx={[-12, 12, 0][i]} cy={[-52, -54, -68][i]} r={[18, 18, 20][i]} fill={c} />)
    )}
  </g>
);

const TREES = [[90, 252, 1.1], [355, 243, 0.85], [700, 240, 1.0]];

// Ground-level details drawn after the back hill (trees, snow caps) and particles drawn on top of everything
export const SeasonTrees = ({ season }) => TREES.map(([x, y, s]) => <Tree key={x} season={season} x={x} y={y} s={s} />);
export const SeasonGround = ({ season, w }) => (season === "winter" ? <SnowCap w={w} /> : null);
export const SeasonAir = ({ season, night, w, h }) => (
  <>
    <Fallers season={season} w={w} h={h} />
    {season === "summer" && night && <Fireflies w={w} />}
  </>
);
